import { useState, useEffect } from 'react';
import classNames from 'classnames';

export interface IButtonProps {
  /**
   * Button content text
   */
  text: string;
  /**
   * The url to link to
   */
  url?: string;
  /**
   * The function to call when the button is clicked
   */
  onClick?: () => void;
  /**
   * Should the link open in a new tab?
   */
  newTab: boolean;
  /**
   * Indicates the border type. Defaults to 'none'.
   */
  border?: string;
  /**
   * Indicates how the button changes when its hovered.
   * Defaults to black background.
   */
  hover?: string;
  /**
   * The background color of the button. Defaults to 'white'.
   */
  backgroundColor?: string;
  /**
   * Additional styles
   */
  className?: string;
}

const borders = {
  none: '',
  black: 'tw-border tw-border-solid tw-border-be-dark-gray',
  white: 'tw-border tw-border-solid tw-border-white',
};

const hovers = {
  black:
    'hover:tw-bg-be-dark-gray active:tw-bg-be-dark-gray focus:tw-bg-be-dark-gray hover:tw-text-white active:tw-text-white focus:tw-text-white',
  white:
    'hover:tw-text-be-dark-gray active:tw-text-be-dark-gray focus:tw-text-be-dark-gray hover:tw-bg-white active:tw-bg-white focus:tw-bg-white',
  darkGreen:
    'hover:tw-text-color-white hover:tw-bg-color-dark-green-light active:tw-text-color-white active:tw-bg-color-dark-green-light tw-underline',
};

const backgrounds = {
  white: 'tw-bg-[#ffffff]',
  transparent: 'tw-bg-transparent',
  darkGreen: `tw-bg-color-dark-green`,
};

const texts = {
  black: 'tw-text-be-dark-gray',
  white: 'tw-text-white',
};

export default function Button({
  text,
  url,
  onClick,
  newTab,
  border = 'none',
  hover = 'black',
  backgroundColor = 'white',
  className,
}: IButtonProps) {
  const [textColor, setTextColor] = useState('');

  function calculateTextColor() {
    if (backgroundColor === 'white' || border === 'black') {
      return texts.black;
    }

    if (border === 'white' || backgroundColor === 'darkGreen') {
      return texts.white;
    }

    return texts.black;
  }

  useEffect(() => {
    setTextColor(calculateTextColor());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hover, backgroundColor, border]);
  const props = onClick ? { onClick, role: 'button', tabIndex: 0 } : { href: url };

  return (
    <a
      {...props}
      type="button"
      target={newTab ? '_blank' : ''}
      className={classNames(
        'tw-font-base tw-inline-block tw-min-w-[240px] tw-h-[44px] tw-px-[16px] tw-py-[12px] tw-text-be-sm tw-leading-[19px] tw-font-normal focus:tw-outline-none tw-w-auto tablet:tw-w-fit tw-no-underline hover:tw-no-underline active:tw-no-underline focus:tw-no-underline tw-text-center tw-cursor-pointer',
        borders[border],
        hovers[hover],
        backgrounds[backgroundColor],
        textColor,
        className
      )}
      rel="noreferrer"
    >
      {text}
    </a>
  );
}
